import type { AcpUsageData, CrowdexSavedAgent } from '@crowdex/agent-contracts';
import { PromptInputButton } from '@crowdex/ui/ai/prompt-input';
import {
  DesktopDropdownContent,
  DesktopDropdownLabel,
  DesktopDropdownRadioItem,
} from '@crowdex/ui/components/desktop';
import {
  DropdownMenu,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@crowdex/ui/components/popover';
import { SegmentedControl, SegmentedControlItem } from '@crowdex/ui/components/segmented-control';
import { Switch } from '@crowdex/ui/components/switch';
import { DESKTOP_MENU_SURFACE } from '@crowdex/ui/lib/desktop-styles';
import { cn } from '@crowdex/ui/lib/utils';
import { Check, ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { AgentAvatar } from '@/components/agent-avatar';
import { useComposerMenuSide } from '@/components/desktop-shell/composer-menu-side';
import { MobileSheetHeader } from '@/components/mobile-shell/mobile-sheet-header';
import { useMediaQuery } from '@/hooks/use-media-query';
import { savedAgentParticipant } from '@/lib/agent-runtime/saved-agents';
import {
  type AcpSelectConfigOption,
  displayChoiceName,
  flattenConfigOptions,
  MODEL_LIST_RETRY,
  modelListState,
} from '@/lib/desktop-config-options';
import { type AgentRuntime, agentsForRuntime } from '@/lib/desktop-shell/cloud-agents';
import { compactUsage } from '@/lib/environment-menu/format';
import type { NativeAgent } from '@/lib/native-desktop';

export type ModelConfigAiAgent = {
  profiles: readonly CrowdexSavedAgent[];
  agents: readonly NativeAgent[];
  selected: CrowdexSavedAgent | null;
  disabled?: boolean;
  onChange: (profile: CrowdexSavedAgent | null) => void;
};

/** An on/off setting of the conversation (Plan first, Team). */
export type ModelConfigToggle = {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
};

export type ModelConfigAgent = {
  id: string;
  name: string | null;
  agents: readonly NativeAgent[];
  discoveryError: string | null;
  disabled?: boolean;
  onChange: (id: string) => void;
  runtime?: AgentRuntime;
};

/** How much of the account's plan the current period used, when the agent reports it. */
export type PlanUsage = {
  label: string;
  /** From 0 to 1. */
  ratio: number;
  /** When it starts over, as said to the user ("Resets in 2 h 50 min"). */
  resets: string | null;
};

export type { AcpSelectConfigOption } from '@/lib/desktop-config-options';
export { flattenConfigOptions } from '@/lib/desktop-config-options';

type ConfigChoice = {
  value: string;
  name: string;
  description?: string | undefined;
};

/** The effort a choice stands for: Claude's implicit `default` is `high`. */
function effectiveValue(option: AcpSelectConfigOption): string {
  const choices = flattenConfigOptions(option);
  if (option.category === 'thought_level' && option.currentValue === 'default')
    return choices.some((choice) => choice.value === 'high') ? 'high' : option.currentValue;
  return option.currentValue;
}

function selectedConfigName(option: AcpSelectConfigOption): string | undefined {
  const value = effectiveValue(option);
  return flattenConfigOptions(option).find((choice) => choice.value === value)?.name;
}

/** A count of tokens as people say it: 101.7k, 1M. */
function compactTokens(value: number): string {
  if (value < 1_000_000) return compactUsage(value);
  const millions = value / 1_000_000;
  return `${Number.isInteger(millions) ? millions : millions.toFixed(1)}M`;
}

/** A section's title inside the popover. */
export function PopoverLabel({ children }: { children: ReactNode }) {
  return <p className="type-meta truncate px-2 pt-1.5 pb-1 text-muted-foreground">{children}</p>;
}

/** A row of the popover that opens its choices beside it: Agent, Runtime, another setting. */
function ChoiceRow({
  children,
  disabled,
  label,
  value,
}: {
  children: ReactNode;
  disabled?: boolean | undefined;
  label: string;
  value: string | undefined;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          className="type-ui flex h-8 w-full items-center gap-2 rounded-md px-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:opacity-50 data-[state=open]:bg-accent"
          type="button"
        >
          <span className="font-medium">{label}</span>
          <span className="ml-auto max-w-40 truncate text-muted-foreground">{value}</span>
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DesktopDropdownContent align="start" className="min-w-52" side="right" sideOffset={8}>
        {children}
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}

function OptionRow({
  disabled,
  onChange,
  option,
}: {
  disabled: boolean;
  onChange: (configId: string, value: string) => void;
  option: AcpSelectConfigOption;
}) {
  const choices = flattenConfigOptions(option);
  return (
    <ChoiceRow disabled={disabled} label={option.name} value={selectedConfigName(option)}>
      <DesktopDropdownLabel className="py-1 font-normal">{option.name}</DesktopDropdownLabel>
      <DropdownMenuRadioGroup
        onValueChange={(value) => onChange(option.id, value)}
        value={option.currentValue}
      >
        {choices.map((choice) => (
          <DesktopDropdownRadioItem
            key={choice.value}
            title={choice.description}
            value={choice.value}
          >
            {choice.name}
          </DesktopDropdownRadioItem>
        ))}
      </DropdownMenuRadioGroup>
      {choices.length === 0 && (
        <div className="type-caption px-2.5 py-2 text-muted-foreground">
          No compatible option is available.
        </div>
      )}
    </ChoiceRow>
  );
}

/** The models, one row each with what it is for (D8). */
/**
 * The chip's word for a model option with no current choice: never the popover's sentence
 * ("Claude Code listed no models"), which is not a model's name.
 */
function modelChipStatus(model: AcpSelectConfigOption): string {
  const state = modelListState(model);
  if (state?.state === 'loading') return 'Loading models…';
  if (state?.state === 'failed') return 'Models unavailable';
  if (state?.state === 'empty' || flattenConfigOptions(model).length === 0) return 'No model';
  return model.name;
}

function ModelChoices({
  disabled,
  model,
  onChange,
}: {
  disabled: boolean;
  model: AcpSelectConfigOption;
  onChange: (configId: string, value: string) => void;
}) {
  const choices: ConfigChoice[] = flattenConfigOptions(model);
  const listState = modelListState(model);
  return (
    <div className="flex flex-col">
      {choices.map((choice) => {
        const checked = choice.value === model.currentValue;
        return (
          <button
            aria-pressed={checked}
            className="flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:opacity-50"
            disabled={disabled}
            key={choice.value}
            onClick={() => onChange(model.id, choice.value)}
            type="button"
          >
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="type-ui truncate font-medium">{choice.name}</span>
              {choice.description !== undefined && (
                <span className="type-caption truncate text-muted-foreground">
                  {choice.description}
                </span>
              )}
            </span>
            {checked && <Check className="size-4 shrink-0 text-foreground" />}
          </button>
        );
      })}
      {/* No model listed: they are loading, could not be read (Retry), or the agent listed none. */}
      {model.description && (
        <div className="flex items-center gap-2 px-2 py-1.5">
          <p className="type-caption min-w-0 flex-1 text-muted-foreground" role="status">
            {model.description}
          </p>
          {listState?.state === 'failed' && (
            <button
              className="type-ui shrink-0 rounded-md px-2 py-1 font-medium hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:opacity-50"
              disabled={disabled}
              onClick={() => onChange(model.id, MODEL_LIST_RETRY)}
              type="button"
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The effort as a segmented control: Low, Medium, High, Extra high, Max. Each segment is as wide
 * as its name at least: a name is never cut, the others share what is left.
 */
function EffortChoices({
  disabled,
  effort,
  onChange,
}: {
  disabled: boolean;
  effort: AcpSelectConfigOption;
  onChange: (configId: string, value: string) => void;
}) {
  const choices = flattenConfigOptions(effort).filter((choice) => choice.value !== 'default');
  return (
    <SegmentedControl
      aria-label={effort.name}
      className="mx-1 mb-1 flex"
      disabled={disabled}
      onValueChange={(value) => onChange(effort.id, value)}
      value={effectiveValue(effort)}
    >
      {choices.map((choice) => (
        <SegmentedControlItem
          className="type-meta h-7 px-2 data-[state=on]:font-semibold"
          key={choice.value}
          title={choice.description}
          value={choice.value}
        >
          {displayChoiceName(choice.name)}
        </SegmentedControlItem>
      ))}
    </SegmentedControl>
  );
}

/** A bar of how full something is, with its percentage and what it means. */
function UsageMeter({
  detail,
  label,
  ratio,
}: {
  detail: string | null;
  label: string;
  ratio: number;
}) {
  const percentage = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  return (
    <div className="flex flex-col gap-1.5 px-2 py-1.5">
      <div className="type-ui flex items-center justify-between gap-3">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground tabular-nums">{percentage}%</span>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-control">
        <div
          className={cn(
            'h-full rounded-full',
            ratio >= 0.95 ? 'bg-destructive' : ratio >= 0.85 ? 'bg-attention' : 'bg-running',
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {detail !== null && <p className="type-caption text-muted-foreground">{detail}</p>}
    </div>
  );
}

function AiAgentChoices({ aiAgent, disabled }: { aiAgent: ModelConfigAiAgent; disabled: boolean }) {
  return (
    <DropdownMenuRadioGroup
      onValueChange={(value) => {
        if (aiAgent.disabled) return;
        if (value === 'default') {
          aiAgent.onChange(null);
          return;
        }
        const profile = aiAgent.profiles.find((item) => `profile:${item.id}` === value);
        if (profile) aiAgent.onChange(profile);
      }}
      value={aiAgent.selected ? `profile:${aiAgent.selected.id}` : 'default'}
    >
      <DesktopDropdownRadioItem
        disabled={disabled || aiAgent.disabled}
        title="The agent as it is."
        value="default"
      >
        Default
      </DesktopDropdownRadioItem>
      {aiAgent.profiles.length > 0 && (
        <>
          <DropdownMenuSeparator className="my-1 bg-border/70" />
          <DesktopDropdownLabel className="py-1 font-normal">Your agents</DesktopDropdownLabel>
        </>
      )}
      {aiAgent.profiles.map((profile) => (
        <DesktopDropdownRadioItem
          disabled={
            disabled ||
            aiAgent.disabled ||
            savedAgentParticipant(profile, aiAgent.agents, 'choice') === null
          }
          key={`profile:${profile.id}`}
          title={profile.description}
          value={`profile:${profile.id}`}
        >
          {profile.name}
        </DesktopDropdownRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );
}

/**
 * Who runs the conversation, under the effort: the agents of its runtime in a menu beside its
 * row, the current one checked. Picking another loads that agent's models above.
 */
function AgentChoices({ agent, disabled }: { agent: ModelConfigAgent; disabled: boolean }) {
  const runtime = agent.runtime ?? 'local';
  const agents = agentsForRuntime(agent.agents, runtime);
  return (
    <>
      <DropdownMenuRadioGroup
        onValueChange={(value) => {
          if (value !== agent.id) agent.onChange(value);
        }}
        value={agent.id ?? ''}
      >
        {agents.map((choice) => (
          <DesktopDropdownRadioItem
            disabled={disabled || agent.disabled === true}
            key={choice.id}
            value={choice.id}
          >
            <AgentAvatar agentId={choice.id} className="size-4 shrink-0" name={choice.name} />
            {choice.name}
          </DesktopDropdownRadioItem>
        ))}
      </DropdownMenuRadioGroup>
      {agents.length === 0 && agent.discoveryError === null && (
        <p className="type-caption px-2 py-1.5 text-muted-foreground" role="status">
          {runtime === 'cloud' ? 'No agent runs in the cloud yet.' : 'No agent on this computer.'}
        </p>
      )}
      {agent.discoveryError !== null && (
        <p className="type-caption px-2 py-1.5 text-destructive" role="alert">
          {agent.discoveryError}
        </p>
      )}
    </>
  );
}

function ToggleRow({ disabled, toggle }: { disabled: boolean; toggle: ModelConfigToggle }) {
  const off = disabled || toggle.disabled === true;
  return (
    <button
      aria-checked={toggle.checked}
      className="type-ui flex h-8 w-full items-center gap-2 rounded-md px-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:opacity-50"
      disabled={off}
      onClick={() => toggle.onChange(!toggle.checked)}
      role="switch"
      title={toggle.description}
      type="button"
    >
      <span className="font-medium">{toggle.label}</span>
      <Switch
        aria-hidden
        checked={toggle.checked}
        className="pointer-events-none ml-auto scale-75"
        disabled={off}
        tabIndex={-1}
      />
    </button>
  );
}

/**
 * The composer's model chip and its popover (D8): the agent profile, the model with what each is
 * for, the effort, the other settings of the conversation and the runtime, in one place, with the
 * conversation's usage (its context window, and the plan's period when the agent reports it).
 * The phone shows the same as a sheet (6).
 */
export function ModelConfigMenu({
  agent,
  aiAgent,
  disabled,
  onChange,
  open: controlledOpen,
  onOpenChange,
  options,
  planUsage = null,
  toggles = [],
  usage = null,
}: {
  agent?: ModelConfigAgent;
  aiAgent?: ModelConfigAiAgent;
  disabled: boolean;
  onChange: (configId: string, value: string) => void;
  /** Controlled, for the header's usage ring that opens it too. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  options: readonly AcpSelectConfigOption[];
  planUsage?: PlanUsage | null;
  /** On/off settings of the conversation. */
  toggles?: readonly ModelConfigToggle[];
  usage?: AcpUsageData | null;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  // On a phone the popover is a sheet (mobile 6), titled "Model" with its ×.
  const touchScreen = useMediaQuery('(hover: none) and (pointer: coarse)');
  const menuSide = useComposerMenuSide();
  const model = options.find((option) => option.category === 'model');
  if (model === undefined && agent === undefined && aiAgent === undefined && options.length === 0)
    return null;
  const modelName =
    model === undefined
      ? undefined
      : flattenConfigOptions(model).find((choice) => choice.value === model.currentValue)?.name;
  const effort = options.find((option) => option.category === 'thought_level');
  const selectedEffort = effort ? selectedConfigName(effort) : undefined;
  const effortName = selectedEffort === undefined ? undefined : displayChoiceName(selectedEffort);
  const permissions = options.find((option) => option.category === 'permission');
  const otherOptions = options.filter(
    (option) => option !== model && option !== effort && option !== permissions,
  );
  // The chip names the model the next turn runs; with none yet, why, in a word or two (the
  // popover says the whole sentence).
  const triggerLabel =
    modelName ?? (model === undefined ? undefined : modelChipStatus(model)) ?? 'Model';
  // Who runs it first: the user's agent (a profile) when one is chosen, else the runtime agent.
  const runnerName = aiAgent?.selected?.name ?? agent?.name ?? undefined;
  const separator = <div className="mx-2 my-1 h-px bg-border/70" />;
  const hasSettings = otherOptions.length > 0 || permissions !== undefined || toggles.length > 0;
  const dot = (
    <span aria-hidden className="shrink-0 text-muted-foreground">
      ·
    </span>
  );

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <PromptInputButton
          aria-label="Configure model"
          className="h-8 min-w-0 max-w-80 shrink-0 gap-1.5 rounded-control bg-control px-3 type-ui hover:bg-control/80"
          disabled={disabled}
          size="sm"
        >
          {/* "Codex · GPT-6.1-Sol · Medium": on a narrow composer the agent gives way first. */}
          {runnerName && (
            <>
              <span className="min-w-0 shrink-[100] truncate text-muted-foreground">
                {runnerName}
              </span>
              {dot}
            </>
          )}
          <span className="min-w-0 truncate font-medium text-foreground">{triggerLabel}</span>
          {effortName && (
            <>
              {dot}
              <span className="shrink-0 text-muted-foreground">{effortName}</span>
            </>
          )}
        </PromptInputButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="Model"
        // Never taller than the room beside the composer: the agents and models scroll, the
        // effort and the rest stay in sight below them.
        className={cn(
          DESKTOP_MENU_SURFACE,
          'flex max-h-(--radix-popover-content-available-height) w-[22.5rem] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden',
        )}
        collisionPadding={12}
        data-model-popover
        side={menuSide}
        sideOffset={8}
      >
        {touchScreen && <MobileSheetHeader onClose={() => setOpen(false)} title="Model" />}
        {model !== undefined && (
          <div className="flex min-h-0 shrink flex-col overflow-y-auto" data-model-popover-list>
            {model !== undefined && (
              <>
                <PopoverLabel>
                  {model.name}
                  {agent?.name ? ` · ${agent.name}` : ''}
                </PopoverLabel>
                <ModelChoices disabled={disabled} model={model} onChange={onChange} />
              </>
            )}
          </div>
        )}
        <div className="flex shrink-0 flex-col">
          {effort !== undefined && (
            <>
              <PopoverLabel>{effort.name}</PopoverLabel>
              <EffortChoices disabled={disabled} effort={effort} onChange={onChange} />
            </>
          )}
          {/* Who runs it, under the effort: a row whose menu lists the agents (D8). */}
          {agent !== undefined && (
            <>
              {(model !== undefined || effort !== undefined) && separator}
              <ChoiceRow disabled={disabled} label="Agent" value={agent.name ?? undefined}>
                <AgentChoices agent={agent} disabled={disabled} />
              </ChoiceRow>
            </>
          )}
          {hasSettings && (
            <>
              {(model !== undefined || effort !== undefined || agent !== undefined) && separator}
              {otherOptions.map((option) => (
                <OptionRow
                  disabled={disabled}
                  key={option.id}
                  onChange={onChange}
                  option={option}
                />
              ))}
              {permissions !== undefined && (
                <OptionRow disabled={disabled} onChange={onChange} option={permissions} />
              )}
              {toggles.map((toggle) => (
                <ToggleRow disabled={disabled} key={toggle.id} toggle={toggle} />
              ))}
            </>
          )}
          {/* D8: Agent, Model, Effort and the usage, one choice of who runs the conversation. The
            user's own agents (profiles), when they have some, are a row of their own. */}
          {aiAgent !== undefined && aiAgent.profiles.length > 0 && (
            <>
              {(model !== undefined || effort !== undefined || hasSettings) && separator}
              <ChoiceRow
                disabled={disabled}
                label="Your agent"
                value={aiAgent.selected?.name ?? 'Default'}
              >
                <AiAgentChoices aiAgent={aiAgent} disabled={disabled} />
              </ChoiceRow>
            </>
          )}
          {(usage !== null || planUsage !== null) && (
            <>
              {touchScreen ? <PopoverLabel>Usage</PopoverLabel> : separator}
              {usage !== null && (
                <UsageMeter
                  detail={`${compactTokens(usage.used)} of ${compactTokens(usage.size)} tokens`}
                  label="Context window"
                  ratio={usage.used / Math.max(usage.size, 1)}
                />
              )}
              {planUsage !== null && (
                <UsageMeter
                  detail={planUsage.resets}
                  label={planUsage.label}
                  ratio={planUsage.ratio}
                />
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
